// Thin runners for the profile-page collection export. All the app wiring
// lives here (supabase, SET_REGISTRY, set-file dynamic imports, photo URLs,
// Blob download); the row/sheet building is pure and tested in
// collectionExport.mjs. SheetJS is imported dynamically inside the XLSX path
// only, so it stays out of the main bundle and the CSV path never loads it.

import { supabase } from '../supabaseClient';
import { SET_REGISTRY } from '../setRegistry';
import { parallelFor } from './cardKey';
import { imagePath, imageUrl } from '../CardImage';
import {
  buildCollectionRows, toCsv, toWorkbookSheets, sheetColWidths, exportFilename,
} from './collectionExport.mjs';
import { saveBlobFile, XLSX_MIME } from './saveBlobFile';
import { fetchAllNotes } from './collectionNotes';

// One query, no set filter — RLS scopes to the signed-in user.
// Private notes are a SECOND query: they live in public.collection_notes, not
// on the collections row (see src/lib/collectionNotes.js for why). Attaching
// them as `private_note` keeps the pure row builder unaware of the split.
// Non-fatal — a notes failure must not cost the collector their whole export.
async function fetchRows(userId) {
  const { data, error } = await supabase
    .from('collections')
    .select('*')
    .eq('user_id', userId);
  if (error) throw new Error("couldn't load your collection");
  const rows = data || [];
  let notes = {};
  try {
    notes = await fetchAllNotes(userId);
  } catch (e) {
    console.warn('[export] private notes unavailable (non-fatal):', e);
  }
  return rows.map((r) => ({ ...r, private_note: notes[r.card_key] || '' }));
}

// Registry order drives sort + tab order; only sets present in the rows load.
async function loadSetData(dbRows) {
  const setIds = [...new Set(dbRows.map((r) => (r.card_key || '').split('__')[0]))].filter(Boolean);
  const setData = {};
  await Promise.all(setIds.map(async (setId) => {
    const meta = SET_REGISTRY.find((s) => s.id === setId);
    if (!meta || !meta.dataFile) return;
    try {
      const mod = await import(`../sets/${meta.dataFile}.js`);
      setData[setId] = { name: meta.name, shortName: meta.shortName, sets: mod.SETS || [] };
    } catch (e) {
      console.warn(`Export: could not load set file for ${setId}:`, e);
    }
  }));
  return { setData, setOrder: SET_REGISTRY.map((s) => s.id) };
}

// List the user's storage folder once and emit URLs only for objects that
// exist — has_photos is one flag, not per-side, so it can't be trusted for
// URLs. Paths/URLs always go through imagePath/imageUrl (legacy
// 2024-chrome-galaxy__ stripping lives there; never hand-build paths).
async function buildPhotoLookup(userId) {
  const names = new Set();
  const PAGE = 1000;
  for (let offset = 0; ; offset += PAGE) {
    const { data, error } = await supabase.storage
      .from('card-images')
      .list(userId, { limit: PAGE, offset });
    if (error || !data) break;
    data.forEach((o) => names.add(o.name));
    if (data.length < PAGE) break;
  }
  return (cardKey, side) => {
    const objectName = imagePath(userId, cardKey, side).split('/').slice(1).join('/');
    return names.has(objectName) ? imageUrl(userId, cardKey, side) : '';
  };
}

async function buildRows(user) {
  const dbRows = await fetchRows(user.id);
  const [{ setData, setOrder }, getPhotoUrl] = await Promise.all([
    loadSetData(dbRows),
    buildPhotoLookup(user.id),
  ]);
  return buildCollectionRows(dbRows, { setData, setOrder, parallelFor, getPhotoUrl });
}

export async function exportCollectionCsv(user, username) {
  const rows = await buildRows(user);
  const csv = toCsv(rows);
  await saveBlobFile(new Blob([csv], { type: 'text/csv;charset=utf-8' }), exportFilename(username, 'csv'));
}

export async function exportCollectionXlsx(user, username) {
  const rows = await buildRows(user);
  const XLSX = await import('xlsx'); // lazy — never touches the main bundle
  const wb = XLSX.utils.book_new();
  for (const sheet of toWorkbookSheets(rows)) {
    const ws = XLSX.utils.aoa_to_sheet(sheet.aoa);
    ws['!cols'] = sheetColWidths(sheet.aoa);
    ws['!freeze'] = 'A2'; // freeze the header row
    XLSX.utils.book_append_sheet(wb, ws, sheet.name);
  }
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  await saveBlobFile(new Blob([out], { type: XLSX_MIME }), exportFilename(username, 'xlsx'));
}
